import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  type DataTableColumn,
} from '../../components/ui/data-table';
import ActionDropdown from '../../components/common/action-dropdown';
import InlineFilter from '../../components/ui/inline-filter';
import { capitalizeFirst } from '../../utils';
import SourceCombobox from '../../components/common/SourceCombobox';

import { ORDER_HISTORY_FILTER_FIELDS } from '../../constants/filters';
import {
  Calendar,
  TrendingUp,
  DollarSign,
  Filter,
  Download,
  RefreshCcw,
} from 'lucide-react';
import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import { tradingAccounts } from '../../constants/accountOptions';
import LoaderOverlay from '../../components/common/LoaderOverlay';

type Trade = {
  id: string;
  userId?: string | number;
  clientName?: string;
  symbol: string;
  type: 'buy' | 'sell';
  volume: number;
  openPrice: number;
  currentPrice?: number | string;
  closePrice: number | string;
  profit: number;
  swap?: number;
  status: 'open' | 'closed';
  openTime: string;
  closeTime: string;
};

const OrderHistory = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>(
    tradingAccounts[0]?.label || ''
  );

  const filterFields = ORDER_HISTORY_FILTER_FIELDS;

  const fetchTradeHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const transformedTrades: Trade[] = [];
      setTrades(transformedTrades);
    } catch (error) {
      console.error('Error fetching trade data:', error);
      setTrades([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => {
    fetchTradeHistory();
  }, [fetchTradeHistory]);

  const columns: DataTableColumn<Trade>[] = [
    {
      id: 'id',
      header: 'Trade ID',
      accessor: (row: Trade) => row.id,
      sortable: true,
      sortValue: (row: Trade) => row.id,
      width: '100px',
    },
    ...(user && user.role === 'super_admin'
      ? ([
          {
            id: 'client',
            header: 'Client',
            accessor: (row: Trade) => row.clientName ?? '-',
            sortable: true,
            sortValue: (row: Trade) => row.clientName ?? '',
            width: '120px',
          },
        ] as DataTableColumn<Trade>[])
      : []),
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: (row: Trade) => row.symbol,
      sortable: true,
      sortValue: (row: Trade) => row.symbol,
      width: '80px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: (row: Trade) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'buy'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {capitalizeFirst(row.type)}
        </span>
      ),
      sortable: true,
      sortValue: (row: Trade) => row.type,
      width: '80px',
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: (row: Trade) => row.volume,
      sortable: true,
      sortValue: (row: Trade) => row.volume,
      width: '60px',
    },
    {
      id: 'openPrice',
      header: 'Open Price',
      accessor: (row: Trade) => row.openPrice,
      sortable: true,
      sortValue: (row: Trade) => row.openPrice,
      width: '80px',
    },
    {
      id: 'currentPrice',
      header: 'Current Price',
      accessor: (row: Trade) => row.currentPrice ?? '-',
      sortable: true,
      sortValue: (row: Trade) =>
        typeof row.currentPrice === 'number' ? row.currentPrice : 0,
      width: '90px',
    },
    {
      id: 'closePrice',
      header: 'Close Price',
      accessor: (row: Trade) => row.closePrice,
      sortable: true,
      sortValue: (row: Trade) =>
        typeof row.closePrice === 'number' ? row.closePrice : 0,
      width: '80px',
    },
    {
      id: 'swap',
      header: 'Swap',
      accessor: (row: Trade) => (
        <span
          className={
            row.swap && row.swap < 0 ? 'text-red-500' : 'text-gray-700'
          }
        >
          {typeof row.swap === 'number' ? `$${row.swap.toFixed(2)}` : '$0.00'}
        </span>
      ),
      sortable: true,
      sortValue: (row: Trade) => row.swap || 0,
      width: '70px',
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: (row: Trade) => (
        <span className={row.profit >= 0 ? 'text-green-600' : 'text-red-600'}>
          ${row.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
      sortValue: (row: Trade) => row.profit,
      width: '90px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row: Trade) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'open'
              ? 'bg-blue-100 text-blue-800'
              : row.status === 'closed'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
          }`}
        >
          {capitalizeFirst(row.status)}
        </span>
      ),
      sortable: true,
      sortValue: (row: Trade) => row.status,
      width: '80px',
    },
    {
      id: 'openTime',
      header: 'Open Time',
      accessor: (row: Trade) => row.openTime,
      sortable: true,
      sortValue: (row: Trade) => row.openTime,
      width: '120px',
    },
    {
      id: 'closeTime',
      header: 'Close Time',
      accessor: (row: Trade) => row.closeTime,
      sortable: true,
      sortValue: (row: Trade) => row.closeTime || '',
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: (row: Trade) => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View trade:', row.id)}
          onEdit={() => console.log('Edit trade:', row.id)}
          onDelete={() => console.log('Delete trade:', row.id)}
        />
      ),
      width: '90px',
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Trading History"
          description="View and analyze your past trades"
        />
      </div>

      <div className="mb-4">
        <SourceCombobox
          items={tradingAccounts.map(a => a.label)}
          value={selectedAccount}
          onChange={setSelectedAccount}
          placeholder="Select trading account"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Trades"
          value={trades.length.toString()}
          change="All time"
          icon={Calendar}
        />

        <StatsCard
          title="Total Volume"
          value={`${trades.reduce((sum, t) => sum + t.volume, 0).toFixed(2)} lots`}
          change="All time"
          icon={DollarSign}
        />

        <StatsCard
          title="Total P&L"
          value={
            trades.reduce((sum, t) => sum + t.profit, 0) >= 0
              ? `+$${trades.reduce((sum, t) => sum + t.profit, 0).toFixed(2)}`
              : `$${trades.reduce((sum, t) => sum + t.profit, 0).toFixed(2)}`
          }
          change="All time"
          icon={TrendingUp}
        />

        <StatsCard
          title="Win Rate"
          value={
            trades.length > 0
              ? `${((trades.filter(t => t.profit > 0).length / trades.length) * 100).toFixed(1)}%`
              : '0%'
          }
          change="All time"
          icon={TrendingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Trade History</CardTitle>
              <CardDescription>
                {user && user.role === 'super_admin'
                  ? 'Complete record of all client trading activities'
                  : 'Complete record of your trading activities'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTradeHistory}
                disabled={isLoading}
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  console.log('Filter button clicked, toggling inline filters');
                  setShowInlineFilters(!showInlineFilters);
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Trades"
          />
          {isLoading ? (
            <LoaderOverlay
              isLoading={isLoading}
              loadingText="Loading order history..."
            />
          ) : (
            <DataTable
              columns={columns}
              data={trades}
              searchValue={searchTerm}
              searchPlaceholder="Search by trade ID, symbol, type, ..."
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              showAddButton={false}
              showExportButton={false}
              showColumnVisibility={true}
              initialPageSize={10}
              initialSortBy={{ id: 'openTime', desc: true }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderHistory;
